import { useState, useRef, useEffect } from 'react';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { FlowAPI } from '../services/api';
import { TestMessage, TestResult } from '../types';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';

interface FlowTestPanelProps {
  flowId: string;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  text: string;
  timestamp: Date;
  nodeId?: string;
  variables?: Record<string, any>;
}

const FlowTestPanel = ({ flowId, onClose }: FlowTestPanelProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentNodeId, setCurrentNodeId] = useState<string>('');
  const [sessionVariables, setSessionVariables] = useState<Record<string, any>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Inicia o teste com uma mensagem inicial
    handleSendMessage('acompanhamento'); // Palavra-chave para iniciar o fluxo
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (message: string = inputText) => {
    if (!message.trim()) return;

    // Adiciona mensagem do usuário
    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      text: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Prepara dados do teste
      const testData: TestMessage = {
        user_input: message,
        current_node: currentNodeId || undefined,
        variables: sessionVariables,
      };

      // Envia para API
      const result: TestResult = await FlowAPI.testFlow(flowId, testData);

      // Adiciona resposta do bot
      const botMessage: ChatMessage = {
        id: `bot_${Date.now()}`,
        type: 'bot',
        text: result.response || 'Sem resposta',
        timestamp: new Date(),
        nodeId: result.next_node,
        variables: result.variables,
      };

      setMessages((prev) => [...prev, botMessage]);
      
      // Atualiza estado da sessão
      setCurrentNodeId(result.next_node || '');
      setSessionVariables(result.variables || {});

    } catch (error: any) {
      console.error('Erro ao testar fluxo:', error);
      
      // Mensagem de erro
      const errorMessage: ChatMessage = {
        id: `error_${Date.now()}`,
        type: 'bot',
        text: `❌ Erro: ${error.message}`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
      toast.error('Erro ao processar mensagem no fluxo');
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleReset = () => {
    setMessages([]);
    setCurrentNodeId('');
    setSessionVariables({});
    handleSendMessage('acompanhamento'); // Reinicia o fluxo
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const MessageBubble = ({ message }: { message: ChatMessage }) => (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
          message.type === 'user'
            ? 'bg-primary-600 text-white'
            : 'bg-gray-200 text-gray-900'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
        <div className="flex items-center justify-between mt-1">
          <span
            className={`text-xs ${
              message.type === 'user' ? 'text-primary-100' : 'text-gray-500'
            }`}
          >
            {formatTimestamp(message.timestamp)}
          </span>
          {message.nodeId && (
            <span
              className={`text-xs font-mono ${
                message.type === 'user' ? 'text-primary-100' : 'text-gray-500'
              }`}
            >
              {message.nodeId.substring(0, 8)}...
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-96 bg-white border-l border-gray-200 flex flex-col max-h-screen">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            🧪 Teste do Fluxo
          </h3>
          <p className="text-sm text-gray-600">
            Simule conversas para validar o fluxo
          </p>
        </div>
        
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Session Info */}
      <div className="p-3 bg-gray-50 border-b border-gray-200 text-xs">
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-600">Nó Atual:</span>
          <span className="font-mono text-gray-900">
            {currentNodeId || 'Início'}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Variáveis:</span>
          <span className="text-gray-900">
            {Object.keys(sessionVariables).length} definida(s)
          </span>
        </div>
        
        {Object.keys(sessionVariables).length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
              Ver variáveis
            </summary>
            <div className="mt-1 p-2 bg-white rounded border text-xs">
              {Object.entries(sessionVariables).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-gray-600">{key}:</span>
                  <span className="font-medium">{String(value)}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <div className="text-4xl mb-2">💬</div>
            <p className="text-sm">
              Iniciando conversa...
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}
        
        {isLoading && (
          <div className="flex justify-start mb-4">
            <div className="bg-gray-200 text-gray-900 px-4 py-2 rounded-lg">
              <LoadingSpinner size="small" color="gray" />
              <span className="ml-2 text-sm">Processando...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-2 mb-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              placeholder="Digite sua mensagem..."
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputText.trim() || isLoading}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex justify-between text-xs">
          <button
            onClick={handleReset}
            disabled={isLoading}
            className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            🔄 Reiniciar conversa
          </button>
          
          <span className="text-gray-500">
            {messages.length} mensagem(s)
          </span>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          <p>💡 Dica: Digite números (1, 2, 3) ou palavras-chave das opções para navegar pelo fluxo.</p>
        </div>
      </div>
    </div>
  );
};

export default FlowTestPanel;